import React from 'react';
import styled from 'styled-components';
import { CanvasItem, Element, Section, TempSettings } from '../../types/types';
import { useTheme } from '../../middleware/ThemeContext';
import { Theme } from '../../utils/themes';
import PreviewElement from './PreviewElement';
import { isYoutubeUrl, getYoutubeEmbedUrl } from '../../utils/youtubeHelper';

const CanvasWrapper = styled.div<{ theme: Theme }>`
  flex: 1;
  padding: 30px;
  min-height: 200px;
  background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
`;

const CanvasContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const SectionContainerWrapper = styled.div<{
    theme: Theme;
    $bgColor?: string;
    $backgroundImage?: string;
    $backgroundOverlay?: string;
    $backgroundOverlayOpacity?: number;
    $hasBackgroundImage?: boolean;
}>`
  background-color: ${({ $bgColor, theme }) => $bgColor || theme.CorporateTheme.colors.filterBG};
  background-image: ${({ $backgroundImage }) => $backgroundImage || 'none'};
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 4px;
  padding: 45px 45px;
  margin-bottom: 15px;
  min-height: 150px;
  position: relative;

    ${({ $backgroundOverlay, $backgroundOverlayOpacity, $hasBackgroundImage }) => $hasBackgroundImage && $backgroundOverlay ? `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: ${$backgroundOverlay};
      opacity: ${$backgroundOverlayOpacity || 1};
      border-radius: 4px;
      pointer-events: none;
      z-index: 1;
    }
  ` : ''}
`;

const SingleColumnContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  position: relative;
  z-index: 2;
`;

const MultiColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  position: relative;
  gap: 15px;
  z-index: 2;
`;

const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #adb5bd;
  font-size: 14px;
`;

const MediaPreview = styled.div<{ $alignment?: string }>`
  width: 100%;
  display: flex;
  justify-content: ${({ $alignment }) =>
        $alignment === 'center' ? 'center' :
            $alignment === 'right' ? 'flex-end' : 'flex-start'};
  
  img, video, iframe {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    display: block;
  }
`;

interface PreviewCanvasProps {
    canvasItems: CanvasItem[];
    pendingChanges: Map<string, TempSettings>;
    primaryFont?: string;
}


const PreviewCanvas: React.FC<PreviewCanvasProps> = ({
    canvasItems,
    pendingChanges,
    primaryFont
}) => {
    const { theme } = useTheme();

    const renderElement = (element: Element, elementPendingChanges: TempSettings) => {
        const mergedProps = { ...element.properties, ...elementPendingChanges };

        // Handle media element specifically
        if (element.type === 'media') {
            const mediaType = mergedProps.mediaType || 'Image';
            const mediaTypePrefix = mediaType.toLowerCase();
            const alignment = mergedProps.textAlignment || mergedProps.alignment || 'left';

            const mediaUrl = mergedProps.signedUrl ||
                mergedProps[`${mediaTypePrefix}Preview`] ||
                mergedProps[`${mediaTypePrefix}ExternalURL`] ||
                mergedProps[`${mediaTypePrefix}Url`] ||
                mergedProps[`${mediaTypePrefix}URL`];

            if (mediaType.toLowerCase() === 'image') {
                return mediaUrl ? (
                    <MediaPreview $alignment={alignment}>
                        <img src={mediaUrl} alt={mergedProps.altText || 'Media'} style={{ maxHeight: 300, width: 'auto' }} />
                    </MediaPreview>
                ) : null;
            } else if (mediaType.toLowerCase() === 'video') {
                return mediaUrl ? (
                    <MediaPreview $alignment={alignment}>
                        {isYoutubeUrl(mediaUrl) ? (
                            <iframe
                                width="560px"
                                height="300px"
                                src={getYoutubeEmbedUrl(mediaUrl) || mediaUrl}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                // style={{ borderRadius: '6px', maxWidth: '100%' }}
                                style={{
                                    maxWidth: '100%',
                                    borderRadius: '6px',
                                    display: 'block',
                                    height:"300px"
                                    // margin: textAlignment === 'center' ? '0 auto' : textAlignment === 'right' ? '0 0 0 auto' : '0',
                                    // cursor: linkURL ? 'pointer' : 'default'
                                }}
                            />
                        ) : (
                            <video controls style={{ borderRadius: '6px', maxHeight: 300, width: 'auto' }}>
                                <source src={mediaUrl} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        )}
                    </MediaPreview>
                ) : null;
            }
        }

        // For all other elements, use PreviewElement
        return (
            <PreviewElement
                key={element.id}
                element={element}
                pendingChanges={elementPendingChanges}
                primaryFont={primaryFont}
            />
        );
    };

    const renderSection = (section: Section) => {
        const sectionKey = section.id;
        const sectionPendingChanges = pendingChanges.get(sectionKey) || {};
        const sectionProperties = { ...section.properties, ...sectionPendingChanges };

        const backgroundColor = sectionProperties.bgColor;
        const backgroundImage = sectionProperties.imagePreview
            ? `url(${sectionProperties.imagePreview})`
            : undefined;
        const backgroundOverlay = sectionProperties.imageOverlayColor;
        const backgroundOverlayOpacity = sectionProperties.imageOverlayOpacity || 1;
        const hasBackgroundImage = Boolean(backgroundImage);

        if (section.type === 'single_column') {
            return (
                <SectionContainerWrapper
                    key={section.id}
                    theme={theme}
                    $bgColor={backgroundColor}
                    $backgroundImage={backgroundImage}
                    $backgroundOverlay={backgroundOverlay}
                    $backgroundOverlayOpacity={backgroundOverlayOpacity}
                    $hasBackgroundImage={hasBackgroundImage}
                >
                    <SingleColumnContent>
                        {section.elements && section.elements.length > 0 ? (
                            section.elements.map((element) => {
                                const elementKey = `${section.id}-single-${element.id}`;
                                const elementPendingChanges = pendingChanges.get(elementKey) || {};
                                return (
                                    <div key={element.id}>
                                        {renderElement(element, elementPendingChanges)}
                                    </div>
                                );
                            })
                        ) : (
                            <EmptyState>Empty section</EmptyState>
                        )}
                    </SingleColumnContent>
                </SectionContainerWrapper>
            );
        }

        if (section.type === 'multi_column' && section.columns) {
            return (
                <SectionContainerWrapper
                    key={section.id}
                    theme={theme}
                    $bgColor={backgroundColor}
                    $backgroundImage={backgroundImage}
                    $backgroundOverlay={backgroundOverlay}
                    $backgroundOverlayOpacity={backgroundOverlayOpacity}
                    $hasBackgroundImage={hasBackgroundImage}
                >
                    <MultiColumnGrid>
                        <ColumnContainer>
                            {section.columns.left && section.columns.left.length > 0 ? (
                                section.columns.left.map((element) => {
                                    const elementKey = `${section.id}-left-${element.id}`;
                                    const elementPendingChanges = pendingChanges.get(elementKey) || {};
                                    return (
                                        <div key={element.id}>
                                            {renderElement(element, elementPendingChanges)}
                                        </div>
                                    );
                                })
                            ) : (
                                <EmptyState>Empty column</EmptyState>
                            )}
                        </ColumnContainer>
                        <ColumnContainer>
                            {section.columns.right && section.columns.right.length > 0 ? (
                                section.columns.right.map((element) => {
                                    const elementKey = `${section.id}-right-${element.id}`;
                                    const elementPendingChanges = pendingChanges.get(elementKey) || {};
                                    return (
                                        <div key={element.id}>
                                            {renderElement(element, elementPendingChanges)}
                                        </div>
                                    );
                                })
                            ) : (
                                <EmptyState>Empty column</EmptyState>
                            )}
                        </ColumnContainer>
                    </MultiColumnGrid>
                </SectionContainerWrapper>
            );
        }

        return null;
    };

    return (
        <CanvasWrapper theme={theme}>
            <CanvasContent>
                {canvasItems.length === 0 ? (
                    <EmptyState>
                        <div>No elements added yet</div>
                    </EmptyState>
                ) : (
                    canvasItems.map((item) => {
                        if ('type' in item && (item.type === 'single_column' || item.type === 'multi_column')) {
                            return renderSection(item as Section);
                        }
                        return null;
                    })
                )}
            </CanvasContent>
        </CanvasWrapper>
    );
};

export default PreviewCanvas;